import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';

import useApi from '../../utils/useApi';
import errorContent from '../../utils/ErrorMessages.js';
import Toaster from '../Toaster/Toaster.jsx';
import Error from '../Error/Error.jsx';
import loadIcon from '../../images/loader-transparent.svg';
import Success from '../Success/Success.jsx';
import 'react-tooltip/dist/react-tooltip.css';
import '../../css/common-components.css';
import '../../css/common-styles.css';

import styles from './ConfigurePropertyGroups.module.css';

/**
 * ConfigurePropertyGroups
 *
 * Allows admin users to configure which HubSpot property groups are visible
 * in the Template Mapping screen. Configuration is saved per portal.
 *
 * Object types: Contact, Company, Deal
 * For each object type, all HubSpot property groups are listed with a toggle
 * to enable or disable them. At least one group must remain enabled per type.
 */
const ConfigurePropertyGroups = () => {
  const api = useApi();

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const popup = urlParams.get('popup');

  // Tab state: 0=contact, 1=company, 2=deal
  const OBJECT_TYPES = ['contact', 'company', 'deal'];
  const icons = [styles.sfUser, styles.sfCompany,styles.sfDeal];
  const OBJECT_LABELS = { contact: 'Contact', company: 'Company', deal: 'Deal' };

  const [activeTab, setActiveTab] = useState(0);

  // Loaded data keyed by objectType: { [objectType]: PropertyGroupItem[] }
  const [groupsByType, setGroupsByType] = useState({});
  const [loadingByType, setLoadingByType] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Search query per object type
  const [searchByType, setSearchByType] = useState({});

  const [toasterMessage, setToasterMessage] = useState({ show: false, message: '', type: 'error' });
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [errorPageConfig, setErrorPageConfig] = useState({});

  const showToaster = (message, type = 'success') => {
    setToasterMessage({ show: true, message, type });
  };

  const hideToaster = () => {
    setToasterMessage({ show: false, message: '', type: 'error' });
  };

  /**
   * Fetches property groups for the given objectType if not already cached.
   */
  const fetchPropertyGroups = useCallback(async (objectType) => {
    if (groupsByType[objectType] || loadingByType[objectType]) return;

    setLoadingByType(prev => ({ ...prev, [objectType]: true }));
    try {
      const response = await api.get('/hubspot/get-property-groups', {
        params: { objectType },
      });

      setGroupsByType(prev => ({
        ...prev,
        [objectType]: response.data.propertyGroups || [],
      }));
    } catch (err) {
      console.error(`Failed to load property groups for ${objectType}:`, err);
      setShowErrorPage(true);
      setErrorPageConfig({
        title: 'Failed to Load Property Groups',
        message: `Unable to retrieve property groups for ${objectType}. Please reload and try again.`,
        buttonText: 'Close',
        onButtonClick: () => window.parent.postMessage(JSON.stringify({ action: 'DONE' }), '*'),
      });
    } finally {
      setLoadingByType(prev => ({ ...prev, [objectType]: false }));
    }
  }, [api, groupsByType, loadingByType]);

  // Load on mount (Contact tab) and on subsequent tab switches
  useEffect(() => {
    const objectType = OBJECT_TYPES[activeTab];
    fetchPropertyGroups(objectType);
  }, [activeTab]);

  /**
   * Toggles a property group's selected state for the current object type.
   * Prevents deselecting the last selected group.
   */
  const handleToggle = (objectType, groupName) => {
    setGroupsByType(prev => {
      const current = prev[objectType] || [];
      const selectedCount = current.filter(g => g.selected).length;
      const group = current.find(g => g.name === groupName);

      // Prevent deselecting the last active group
      if (group?.selected && selectedCount === 1) {
        showToaster('At least one property group must remain enabled.', 'error');
        return prev;
      }

      return {
        ...prev,
        [objectType]: current.map(g =>
          g.name === groupName ? { ...g, selected: !g.selected } : g
        ),
      };
    });
  };

  /**
   * Clears (disables) all toggles across all tabs.
   * This does NOT persist — the user must click Save to commit.
   */
  const handleClear = () => {
    setGroupsByType(prev => {
      const updated = { ...prev };
      for (const objectType of OBJECT_TYPES) {
        if (updated[objectType]) {
          updated[objectType] = updated[objectType].map(g => ({...g,selected: g.name === 'contactinformation' || g.name === 'companyinformation' || g.name === 'dealinformation' || g.name === 'deal_information' || g.name === 'deal_information' || g.name === 'deal_revenue'}))
        }
      }
      return updated;
    });
    setSearchByType({});
  };

  /**
   * Saves the configuration for ALL object types in one API call.
   */
  const handleSave = async () => {
    // Validate: each loaded type must have at least one selected group
    for (const objectType of OBJECT_TYPES) {
      const groups = groupsByType[objectType];
      if (groups && groups.filter(g => g.selected).length === 0) {
        showToaster(`At least one property group must be selected for ${OBJECT_LABELS[objectType]}.`, 'error');
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        allowedPropertyGroups: OBJECT_TYPES
          .filter(objectType => groupsByType[objectType]) // only include loaded types
          .map(objectType => ({
            objectType,
            propertyGroups: (groupsByType[objectType] || [])
              .filter(g => g.selected)
              .map(g => g.name),
          })),
      };

      var response = await api.post('/boldsign/update-property-groups', payload);
      if (response.status !== 200) {
        // Show Error page immediately on bad request/server failure
        setErrorPageConfig({
          title: errorContent.default.title,
          message: errorContent.default.message
        });
        setShowErrorPage(true);
        return;
      }

      if (response.status === 200) {
        // Redirect to template create success page
        window.location.href = '/propertyConfigureSuccess' + (popup ? "?popup=1" : "");
        return;
      }
    } catch (err) {
      console.error('Failed to save property groups:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const currentObjectType = OBJECT_TYPES[activeTab];
  const currentGroups = groupsByType[currentObjectType] || [];
  const isCurrentLoading = loadingByType[currentObjectType];
  const currentSearch = (searchByType[currentObjectType] || '').toLowerCase();

  // Search-filtered groups for the current tab
  const filteredGroups = useMemo(() => {
    if (!currentSearch) return currentGroups;
    return currentGroups.filter(g =>
      g.displayName.toLowerCase().includes(currentSearch) ||
      g.name.toLowerCase().includes(currentSearch)
    );
  }, [currentGroups, currentSearch]);

  const enabledCount = currentGroups.filter(g => g.selected).length;
  const totalCount = currentGroups.length;

  if (showErrorPage) {
    return (
      <Error
        title={errorPageConfig.title}
        message={errorPageConfig.message}
        buttonText={errorPageConfig.buttonText}
        onButtonClick={errorPageConfig.onButtonClick}
      />
    );
  }

  return (
    <div className={styles.templateConfigureContainer}>
     <div className={styles.templateSelectWrapper}>
        <div className={styles.container}>
          <div className={styles.templateHeader}>
            <h6 className={styles.titleHeader}>Enable Property Groups</h6>
            <div className={styles.actionButtons}>
              <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              disabled={isSaving}
            >
              Clear
            </button>
            <button
                type="button"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={isSaving}
            >
              {isSaving ? (
                <>
                  Saving...
                </>
              ) : (
                'Save'
              )}
              </button>
            {isSaving && (
              <div className="loading-overlay">
                <img src={loadIcon} alt="loading" className="spinner-circle"/>
              </div>
            )}
            </div>
          </div>
        <div className={styles.templateTableContainer}>
      {/* Object Type Tabs */}
      <div className={styles.tabs}>
        <div className={styles.tabOuter}>
          {OBJECT_TYPES.map((objectType, idx) => (
            <div key={objectType} className={`${styles.tabContainer} ${activeTab === idx ? styles.tabActive : ''}`}>
              <span className={`${styles.sfIconBase} ${icons[idx]}`}></span>
              <button
              key={objectType}
              className={`${styles.tab} ${activeTab === idx ? styles.buttonActive : ''}`}
              onClick={() => setActiveTab(idx)}
              type="button"
            >
              <span className={styles.objects}>{OBJECT_LABELS[objectType]}</span>
            </button>
            </div>
          ))}
        </div>
      </div>
          <div className={styles.container}>

      {/* Property Groups Content */}
      <div className={styles.content}>
        <div className={styles.infoContainer}>
          <div className={styles.infoMessage}>
            <span className={`${styles.sfIconBase} ${styles.infoIcon} sfInfoIcon`} aria-hidden="true"></span>
            <span>Enable property groups to make their properties available for field mapping in BoldSign templates.</span>
          </div>
            <a
              className={styles.learnMoreLink}
              href="https://support.boldsign.com/kb/article/25334"
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            <span className={styles.sfIconOpenLink}></span>
            </a>
        </div>
        {isCurrentLoading ? (
          <div className={styles.loadingContainer}>
            <img src={loadIcon} alt="Loading" className={styles.loadingIcon} />
            <span className={styles.loadingText}>Loading property groups...</span>
          </div>
        ) : (
          <>
            {/* Search and count bar */}
            <div className={styles.toolbar}>
              <div className={styles.searchWithButtonContainer}>
              <div className={styles.templateSearchContainer}>
                <span className={`${styles.sfSearchIcon} ${styles.sfSearch} ${styles.sfIconBase}`}></span>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search property groups..."
                  value={searchByType[currentObjectType] || ''}
                  onChange={e =>
                    setSearchByType(prev => ({ ...prev, [currentObjectType]: e.target.value }))
                  }
                  />
              </div>
              </div>
              {totalCount > 0 && (
                <div className={styles.propertiesCount}>
                  <span className={styles.enabledCount}>
                  {enabledCount} of {totalCount} property groups enabled
                  </span>
                  <button className={`${styles.propertyHelpButton} help-button`}>
                      <span className = {`${styles.sfIconBase} ${styles.signingOrderinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="so-tooltip"></span>
                    </button>
                    <Tooltip
                      id="so-tooltip"
                      place="top"
                      className={`tooltip-info tip-enabledProperty`}
                      render={() => (
                        <div className="tooltip-content">
                          These properties only available in templates field mapping.
                        </div>
                      )}
                  />
                </div>
              )}
            </div>

            {filteredGroups.length === 0 ? (
              <div className={styles.emptyState}>
                <p>
                  {currentSearch
                    ? 'No property groups match your search.'
                    : `No property groups found for ${OBJECT_LABELS[currentObjectType]}.`}
                </p>
              </div>
            ) : (
              <div className={styles.groupList}>
                <div className={styles.groupListHeader}>
                  <span className={styles.colName}>Property Group</span>
                  <span className={styles.colEnabled}><span>Available for Mapping</span> 
                    <button className={`${styles.propertyHelpButton} help-button`}>
                      <span className = {`${styles.sfIconBase} ${styles.signingOrderinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="toggle-tooltip"></span>
                    </button>
                    <Tooltip
                      id="toggle-tooltip"
                      place="top"
                      className={`tooltip-info tip-enabledProperty`}
                      render={() => (
                        <div className="tooltip-content">
                          Enabled property group will be available in template field mapping
                        </div>
                      )}
                  />
                    </span>
                </div>
                {filteredGroups.map(group => (
                  <div key={group.name} className={styles.groupRow}>
                    <div className={styles.colName}>
                      <span className={styles.groupDisplayName}>{group.displayName}</span>
                      <span className={styles.groupInternalName}>({group.propertyCount} properties)</span>
                    </div>
                    <div className={`${styles.colEnabled} ${styles.toggle}`}>
                      <label
                        className={`${styles.toggleSwitch}`}
                        aria-label={`Toggle ${group.displayName}`}
                      >
                        <input
                          type="checkbox"
                          checked={group.selected}
                          onChange={() => handleToggle(currentObjectType, group.name)}
                        />
                        <span className={styles.toggleSlider} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        
      </div>

      {toasterMessage.show && (
        <Toaster
          show={true}
          message={toasterMessage.message}
          type={toasterMessage.type}
          onClose={hideToaster}
        />
      )}
    </div>
                                    </div>
              </div>
             </div>
        </div>
  );
};

export default ConfigurePropertyGroups;
